'use client';

import { useMemo, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface PhoneInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  /** Current value - can be in any format, will be normalized */
  value?: string;
  /** Called when value changes - returns normalized +61XXXXXXXXX format */
  onChange?: (value: string) => void;
  /** Called when a complete valid number is entered */
  onComplete?: (value: string) => void;
  /** Additional class names */
  className?: string;
}

/**
 * Normalize any phone input to raw digits after the country code
 * Handles: +61412345678, 0412345678, 61412345678, 412345678, etc.
 */
function normalizeToDigits(input: string): string {
  // If input starts with +61, extract digits after it (our canonical format)
  if (input.startsWith('+61')) {
    return input.slice(3).replace(/\D/g, '');
  }

  // Remove all non-digit characters
  const digits = input.replace(/\D/g, '');

  // Handle various prefixes for external input (paste, initial value)
  if (digits.startsWith('61') && digits.length >= 11) {
    // 61 prefix with full number - remove it
    return digits.slice(2);
  }
  if (digits.startsWith('0') && digits.length >= 10) {
    // Local format starting with 0 - remove the leading 0
    return digits.slice(1);
  }
  if (digits.length === 9 && /^[2-478]/.test(digits)) {
    // Already 9 digits starting with valid area code
    return digits;
  }

  // Return as-is if we can't normalize (handles partial input)
  return digits;
}

/**
 * Format 9 digits into display format: X XXXX XXXX
 * Works for both mobile (4XX XXX XXX) and landline (X XXXX XXXX)
 */
function formatPhoneDigits(digits: string): string {
  const cleaned = digits.slice(0, 9);

  if (cleaned.length === 0) return '';
  if (cleaned.length <= 1) return cleaned;
  if (cleaned.length <= 5) return `${cleaned.slice(0, 1)} ${cleaned.slice(1)}`;
  return `${cleaned.slice(0, 1)} ${cleaned.slice(1, 5)} ${cleaned.slice(5)}`;
}

/**
 * Convert formatted position to raw position (account for spaces)
 */
function formattedToRawPosition(formatted: string, cursorPos: number): number {
  let rawPos = 0;
  for (let i = 0; i < cursorPos && i < formatted.length; i++) {
    if (formatted[i] !== ' ') {
      rawPos++;
    }
  }
  return rawPos;
}

/**
 * Convert raw position to formatted position (account for spaces)
 */
function rawToFormattedPosition(rawPos: number): number {
  // Format is X XXXX XXXX
  // Spaces are at positions 1 and 6 in formatted string
  if (rawPos <= 1) return rawPos;
  if (rawPos <= 5) return rawPos + 1; // After first space
  return rawPos + 2; // After both spaces
}

/**
 * Validate if a digit is valid at a given position
 * Position 0: must be 2, 3, 4, 7, or 8 (area codes)
 */
function isValidDigitAtPosition(digit: string, position: number): boolean {
  if (!/^\d$/.test(digit)) return false;
  if (position === 0) {
    // First digit must be a valid area code: 2, 3, 4, 7, 8
    return /^[2-478]$/.test(digit);
  }
  return true;
}

/**
 * PhoneInput component
 *
 * A specialized input for Australian phone numbers that:
 * - Displays numbers in +61 X XXXX XXXX format
 * - Accepts input in any format (local, international, with/without spaces)
 * - Auto-formats as user types
 * - Validates Australian mobile (04) and landline (02, 03, 07, 08) numbers
 */
export const PhoneInput = ({
  ref,
  value = '',
  onChange,
  onComplete,
  className,
  placeholder,
  ...props
}: PhoneInputProps & { ref?: React.Ref<HTMLInputElement> }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  // Derive display value from the value prop
  const displayValue = useMemo(
    () => formatPhoneDigits(normalizeToDigits(value)),
    [value]
  );

  const updateValue = (newDigits: string, newCursorRawPos: number) => {
    // Return full normalized value with +61 prefix
    const fullValue = newDigits.length > 0 ? `+61${newDigits}` : '';
    onChange?.(fullValue);

    if (newDigits.length === 9) {
      onComplete?.(fullValue);
    }

    // Set cursor position
    requestAnimationFrame(() => {
      if (inputRef.current) {
        const newFormattedPos = rawToFormattedPosition(newCursorRawPos);
        inputRef.current.setSelectionRange(newFormattedPos, newFormattedPos);
      }
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = inputRef.current;
    if (!input) return;

    const selectionStart = input.selectionStart ?? 0;
    const selectionEnd = input.selectionEnd ?? 0;
    const hasSelection = selectionStart !== selectionEnd;

    const rawCursorPos = formattedToRawPosition(displayValue, selectionStart);
    const currentDigits = normalizeToDigits(value);

    if (e.key === 'Backspace') {
      e.preventDefault();

      if (hasSelection) {
        const rawStart = formattedToRawPosition(displayValue, selectionStart);
        const rawEnd = formattedToRawPosition(displayValue, selectionEnd);
        const newDigits =
          currentDigits.slice(0, rawStart) + currentDigits.slice(rawEnd);
        updateValue(newDigits, rawStart);
      } else if (rawCursorPos > 0) {
        const newDigits =
          currentDigits.slice(0, rawCursorPos - 1) +
          currentDigits.slice(rawCursorPos);
        updateValue(newDigits, rawCursorPos - 1);
      }
      return;
    }

    if (e.key === 'Delete') {
      e.preventDefault();

      if (hasSelection) {
        const rawStart = formattedToRawPosition(displayValue, selectionStart);
        const rawEnd = formattedToRawPosition(displayValue, selectionEnd);
        const newDigits =
          currentDigits.slice(0, rawStart) + currentDigits.slice(rawEnd);
        updateValue(newDigits, rawStart);
      } else if (rawCursorPos < currentDigits.length) {
        const newDigits =
          currentDigits.slice(0, rawCursorPos) +
          currentDigits.slice(rawCursorPos + 1);
        updateValue(newDigits, rawCursorPos);
      }
      return;
    }

    // Handle digit input
    if (/^\d$/.test(e.key) && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();

      const digit = e.key;

      if (hasSelection) {
        const rawStart = formattedToRawPosition(displayValue, selectionStart);
        const rawEnd = formattedToRawPosition(displayValue, selectionEnd);

        if (isValidDigitAtPosition(digit, rawStart)) {
          const newDigits =
            currentDigits.slice(0, rawStart) +
            digit +
            currentDigits.slice(rawEnd);
          updateValue(newDigits.slice(0, 9), rawStart + 1);
        }
      } else if (currentDigits.length < 9) {
        // Insert mode
        if (isValidDigitAtPosition(digit, rawCursorPos)) {
          const newDigits =
            currentDigits.slice(0, rawCursorPos) +
            digit +
            currentDigits.slice(rawCursorPos);
          updateValue(newDigits.slice(0, 9), rawCursorPos + 1);
        }
      } else {
        // Replace mode - value is full
        if (rawCursorPos < 9 && isValidDigitAtPosition(digit, rawCursorPos)) {
          const newDigits =
            currentDigits.slice(0, rawCursorPos) +
            digit +
            currentDigits.slice(rawCursorPos + 1);
          updateValue(newDigits, rawCursorPos + 1);
        }
      }
      return;
    }

    // Ignore non-digit printable characters
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      return;
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    const input = inputRef.current;
    if (!input) return;

    const pastedText = e.clipboardData.getData('text');
    const selectionStart = input.selectionStart ?? 0;
    const selectionEnd = input.selectionEnd ?? 0;

    const currentDigits = normalizeToDigits(value);
    const rawStart = formattedToRawPosition(displayValue, selectionStart);
    const rawEnd = formattedToRawPosition(displayValue, selectionEnd);

    // Normalize the pasted text to get the digits
    const pastedDigits = normalizeToDigits(pastedText);

    if (pastedDigits.length > 0) {
      // Filter to only valid digits at each position
      let validDigits = '';
      for (let i = 0; i < pastedDigits.length && rawStart + i < 9; i++) {
        const digit = pastedDigits[i];
        if (isValidDigitAtPosition(digit, rawStart + validDigits.length)) {
          validDigits += digit;
        }
      }

      if (validDigits.length > 0) {
        const newDigits =
          currentDigits.slice(0, rawStart) +
          validDigits +
          currentDigits.slice(rawEnd);
        updateValue(newDigits.slice(0, 9), rawStart + validDigits.length);
      }
    }
  };

  const handleChange = () => {
    // Intentionally empty - we handle all changes in handleKeyDown/handlePaste
  };

  // Merge refs
  const mergedRef = (node: HTMLInputElement | null) => {
    inputRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  };

  return (
    <div className="flex items-center">
      <span className="text-muted-foreground mr-2 text-sm font-medium select-none">
        +61
      </span>
      <Input
        {...props}
        ref={mergedRef}
        type="text"
        inputMode="numeric"
        value={displayValue}
        placeholder={placeholder || '4XX XXX XXX'}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        className={cn('flex-1', className)}
      />
    </div>
  );
};
